import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";

export type DirtyViewScope = "portfolio" | "leaderboard" | "home";
export type DirtyViewSource = "order_submit" | "order_update" | "trade_event";

export interface DirtyViewState {
  dirty: boolean;
  dirtyAt: number;
  source: DirtyViewSource;
}

type DirtyViewBroadcastPayload = {
  event: "dirty_mark" | "dirty_clear";
  scope: DirtyViewScope;
  poolCode: string;
  source: DirtyViewSource;
  dirtyAt: number;
};

const DIRTY_VIEWS_BROADCAST_CHANNEL = "sportex100-dirty-views";
const DIRTY_VIEWS_STORAGE_KEY = "sportex100.dirty-views.event";

const canUseBroadcastChannel =
  typeof window !== "undefined" && typeof window.BroadcastChannel !== "undefined";

function publishDirtyEvent(payload: DirtyViewBroadcastPayload) {
  if (typeof window === "undefined") {
    return;
  }

  if (canUseBroadcastChannel) {
    const channel = new window.BroadcastChannel(DIRTY_VIEWS_BROADCAST_CHANNEL);
    channel.postMessage(payload);
    channel.close();
    return;
  }

  try {
    window.localStorage.setItem(
      DIRTY_VIEWS_STORAGE_KEY,
      JSON.stringify({ ...payload, publishedAt: Date.now() }),
    );
    window.localStorage.removeItem(DIRTY_VIEWS_STORAGE_KEY);
  } catch {
    // Best-effort cross-tab sync only.
  }
}

function applyDirtyEvent(queryClient: QueryClient, payload: DirtyViewBroadcastPayload) {
  if (!payload.poolCode) {
    return;
  }

  if (payload.event === "dirty_mark") {
    queryClient.setQueryData<DirtyViewState>(queryKeys.dirtyView(payload.scope, payload.poolCode), {
      dirty: true,
      dirtyAt: payload.dirtyAt,
      source: payload.source,
    });
    return;
  }

  queryClient.setQueryData<DirtyViewState>(
    queryKeys.dirtyView(payload.scope, payload.poolCode),
    (previous) => ({
      dirty: false,
      dirtyAt: payload.dirtyAt,
      source: previous?.source ?? payload.source,
    }),
  );
}

export function subscribeDirtyViewCrossTab(queryClient: QueryClient) {
  if (typeof window === "undefined") {
    return () => {};
  }

  if (canUseBroadcastChannel) {
    const channel = new window.BroadcastChannel(DIRTY_VIEWS_BROADCAST_CHANNEL);
    channel.onmessage = (event: MessageEvent<DirtyViewBroadcastPayload>) => {
      const payload = event.data;
      if (!payload?.scope || !payload?.poolCode) {
        return;
      }
      applyDirtyEvent(queryClient, payload);
    };
    return () => {
      channel.close();
    };
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key !== DIRTY_VIEWS_STORAGE_KEY || !event.newValue) {
      return;
    }

    try {
      const parsed = JSON.parse(event.newValue) as DirtyViewBroadcastPayload;
      if (!parsed?.scope || !parsed?.poolCode) {
        return;
      }
      applyDirtyEvent(queryClient, parsed);
    } catch {
      // Ignore malformed payloads.
    }
  };

  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("storage", onStorage);
  };
}

export function markDirty(
  queryClient: QueryClient,
  scope: DirtyViewScope,
  poolCode: string | null,
  source: DirtyViewSource,
) {
  if (!poolCode) return;

  const dirtyAt = Date.now();

  queryClient.setQueryData<DirtyViewState>(queryKeys.dirtyView(scope, poolCode), {
    dirty: true,
    dirtyAt,
    source,
  });

  publishDirtyEvent({
    event: "dirty_mark",
    scope,
    poolCode,
    source,
    dirtyAt,
  });
}

export function isDirty(queryClient: QueryClient, scope: DirtyViewScope, poolCode: string | null) {
  if (!poolCode) return false;
  const state = queryClient.getQueryData<DirtyViewState>(queryKeys.dirtyView(scope, poolCode));
  return state?.dirty === true;
}

export function clearDirty(queryClient: QueryClient, scope: DirtyViewScope, poolCode: string | null) {
  if (!poolCode) return;

  const dirtyAt = Date.now();

  queryClient.setQueryData<DirtyViewState>(queryKeys.dirtyView(scope, poolCode), (previous) => ({
    dirty: false,
    dirtyAt,
    source: previous?.source ?? "trade_event",
  }));

  publishDirtyEvent({
    event: "dirty_clear",
    scope,
    poolCode,
    source: "trade_event",
    dirtyAt,
  });
}
